import { Download } from 'generated/graphql';
import type { ExerciseSwapConfig } from '@client/new/features/exercise-swaps';
import { ParentExerciseType } from '../../workout-editor/model/workout.domain';

export interface Media {
  audio?: string;
  audioUrl?: string;
  cdn01?: string;
  downloadLink?: Download;
  height?: number;
  image?: string;
  mimeType?: string;
  source?: string;
  uuid?: string;
  video?: string;
  videoSourceType?: string;
  videoThumbnailUrl?: string;
  videoUrl?: string;
  width?: number;
  duration?: number;
  title?: string;
  description?: string;
  type?: 'long' | 'short';
}

export const setModes = ['fixed', 'range', 'minimum', 'toFailure'] as const;
export type SetMode = (typeof setModes)[number];

export interface Set {
  reps: (number | string)[];
  mode?: SetMode;
  repsMax?: number | string | null;
  uuid?: string;
  time?: number;
  rest?: number;
}

export type LegacyExercise = {
  id: string;
  uuid?: string;
  type: number;
  name: string;
  description?: string;
  media?: Media | null;
  rest?: number | null;
  unilateral?: boolean | null;
  sets: Array<Set>;
  swapConfig?: ExerciseSwapConfig;
};

export type Autoplayed<T extends number> = {
  id: string;
  uuid: string;

  type: T;
  name: string;
  description?: string;
  media?: Media;
  duration?: [number, string];
  rest?: number | null;
  exercises?: Array<LegacyExercise>;
  longRest?: number;
  rounds?: number;
};

export type Circuit = Autoplayed<3> & {
  measurementMode?: MeasurementMode;
};
export type LegacyTabata = Autoplayed<10>;
export type LegacyHIIT = Autoplayed<11>;

export type Video = {
  uuid: string;
  name: string;
  description?: string;
  type: number;
  media?: Media;
  youtubeId?: string;
};

export enum RunningExerciseType {
  Treadmill = 'Treadmill',
  Outdoor = 'Outdoor',
}

export enum RunningTempo {
  Walk = 'Walk',
  Jog = 'Jog',
  Run = 'Run',
  Sprint = 'Sprint',
  Power = 'Power',
}

export type RunningExerciseSet = {
  [RunningExerciseType.Outdoor]: {
    uuid: string;
    distance: number; // float with one decimal. Miles
    tempo: RunningTempo;
    audio?: Media;
    time: number; // seconds
    uploading?: boolean;
  };
  [RunningExerciseType.Treadmill]: {
    uuid: string;
    speed: number; // float with 1 decimal. Mph
    incline: number; // 1-15. %
    audio?: Media;
    time: number; // seconds
    uploading?: boolean;
  };
};

export const maxWorkoutDurationMinutes = 600;

export const measurementModes = ['time-based', 'distance-based'] as const;
export type MeasurementMode = (typeof measurementModes)[number];

export type Running<T extends RunningExerciseType> = {
  measurementMode?: MeasurementMode;
  uuid?: string;
  name: string;
  type: number;
  variant?: T;
  sets: RunningExerciseSet[T][];
  media?: Media;
  uploading?: boolean;
};
export type RunningOutdoor = Running<RunningExerciseType.Outdoor>;

export type WorkoutItem = (
  | LegacyExercise
  | Circuit
  | Video
  | RunningOutdoor
  | Running<RunningExerciseType.Treadmill>
  | LegacyHIIT
  | LegacyTabata
) & { id?: string };

export type Template =
  | 'exercise'
  | 'circuit'
  | 'video'
  | 'running'
  | ParentExerciseType;

export type WorkoutType = 'workout' | 'class' | 'rest';

export const isEmptyRestDay = (workout: {
  type: WorkoutType;
  exercises: unknown[];
}) => workout.type === 'rest' && workout.exercises.length === 0;

export const parseWorkoutType = (raw?: string | null): WorkoutType =>
  raw === 'class' || raw === 'rest' ? raw : 'workout';
